/**
 * The dashboard's live-data channel.
 *
 * WHY AN EXTERNAL STORE RATHER THAN PAGE STATE
 * --------------------------------------------
 * The live panels need to repaint once a second so call timers and
 * time-in-state counters advance smoothly. The obvious way to do that is a
 * `useState` tick on the page — which is what this page did, and it was the
 * dominant cause of the dashboard feeling laggy.
 *
 * The problem is not the panels. It is everything ELSE that a page render
 * drags along with it: `WidthProvider(Responsive)` re-measures, and
 * react-grid-layout re-renders all ten `GridItem`s and recomputes their
 * positions and transforms — every single second, forever, whether or not
 * anything moved. Memoising the panels cannot prevent that, because the grid
 * sits ABOVE them in the tree; by the time memo gets a say, the expensive work
 * has already happened.
 *
 * Publishing live data through an external store inverts the flow. The page
 * renders only when something structural changes (a filter, the 5-second
 * history refresh, a layout edit). The per-second updates are delivered
 * straight to the handful of components that subscribe, so the grid never sees
 * them.
 *
 * `useSyncExternalStore` is the correct primitive here rather than a hand-rolled
 * subscribe/setState: it is tear-free under concurrent rendering, and it gives
 * React the chance to batch subscribers into a single commit.
 */
import { useSyncExternalStore } from 'react'
import type { SupervisorAgentSummary, SupervisorLiveCall } from '@kc3/types'

export interface LiveSnapshot {
  agents: SupervisorAgentSummary[]
  calls: SupervisorLiveCall[]
}

const EMPTY: LiveSnapshot = { agents: [], calls: [] }

/**
 * A cheap fingerprint of everything the tables actually RENDER.
 *
 * The simulator rebuilds its projections every tick, so the arrays arrive with
 * fresh object identities once a second even when every field is unchanged.
 * Publishing those blindly re-rendered whole tables to redraw nothing.
 *
 * Elapsed times are deliberately ABSENT from this signature: they are rendered
 * by `<Elapsed>`, which subscribes to the clock on its own. That omission is
 * what lets an idle floor settle into zero table renders while its timers keep
 * counting.
 */
function signature(agents: SupervisorAgentSummary[], calls: SupervisorLiveCall[]): string {
  let sig = ''
  for (const a of agents) {
    sig += `${a.id}|${a.status}|${a.statusSince ?? ''}|${a.callsHandled}|${a.callDurationSec}|${a.currentCallId ?? ''}|${a.currentQueueId ?? ''};`
  }
  sig += '#'
  for (const c of calls) {
    sig += `${c.callId}|${c.agentId}|${c.startedAt}|${c.queueId ?? ''}|${c.status ?? ''};`
  }
  return sig
}

let lastSignature = ''

let snapshot: LiveSnapshot = EMPTY
const listeners = new Set<() => void>()

/**
 * Publish live data, but ONLY if it differs from what is already on screen.
 *
 * The early return is the load-bearing part. Without it an idle floor still
 * re-rendered every table once a second, because the incoming arrays were new
 * objects describing identical state.
 *
 * When it does publish it always publishes a NEW object:
 * `useSyncExternalStore` compares snapshots by identity, so mutating in place
 * would be silently ignored.
 */
export function publishLive(next: LiveSnapshot): void {
  const sig = signature(next.agents, next.calls)
  if (sig === lastSignature) return
  lastSignature = sig
  snapshot = { agents: next.agents, calls: next.calls }
  for (const listener of listeners) listener()
}

export function subscribeLive(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getLiveSnapshot(): LiveSnapshot {
  return snapshot
}

/** Reset between tests / on unmount of the last driver. */
export function resetLive(): void {
  snapshot = EMPTY
  lastSignature = ''
  listeners.clear()
}

/**
 * Subscribe to live agents and calls.
 *
 * Re-renders the caller only when the DATA changes — not on the clock. For a
 * ticking duration use `<Elapsed>` / `useNow()` from `./clock-store.ts`, which
 * updates the individual timer without disturbing the table around it.
 */
export function useLive(): LiveSnapshot {
  return useSyncExternalStore(subscribeLive, getLiveSnapshot, getLiveSnapshot)
}
